import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { Item, ItemLabel } from '../../../../components/ActionMenu/ActionMenu.styles';
import { STATUS_COLORS } from '../../../../theme/tokens';
import { usePortalMenu } from '../../usePortalMenu';
import { Panel, Trigger } from './StyleCardMenu.styles';

export type StyleCardMenuProps = {
  /** The style's display name (names the trigger for assistive tech). */
  styleName: string;
  disabled?: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  /** Called with `true` when the list opens and `false` when it closes. */
  onOpenChange?: (open: boolean) => void;
  className?: string;
};

/**
 * A style card's small menu (Duplicate, Delete). The shared ActionMenu positions its list inside the trigger's
 * box, which the strip's horizontal scroller would clip, so this one portals the list to the body and places
 * it from the trigger (right-aligned, below, or above when below has no room), following the trigger while the
 * page or the strip scrolls (see `usePortalMenu`). Escape, a click outside and choosing an item close it; focus
 * returns to the trigger, and the arrow keys move between items.
 */
export const StyleCardMenu = ({
  styleName,
  disabled = false,
  onDuplicate,
  onDelete,
  onOpenChange,
  className,
}: StyleCardMenuProps): React.ReactElement => {
  const { t } = useTranslation();
  const { open, setOpen, close, place, triggerRef, panelRef, onItemKeyDown, panelStyle } =
    usePortalMenu<HTMLButtonElement>({ align: 'end', onOpenChange });

  const choose = (action: () => void) => (): void => {
    close(true);
    action();
  };

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        className={className}
        $open={open}
        aria-label={t('Actions for {{name}}', { name: styleName })}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(!open);
        }}
      >
        <Icon name="more" size={16} />
      </Trigger>
      {open
        ? createPortal(
            <Panel
              ref={panelRef}
              role="menu"
              $shown={place !== null}
              style={panelStyle}
              onKeyDown={onItemKeyDown}
            >
              <Item type="button" role="menuitem" $destructive={false} onClick={choose(onDuplicate)}>
                <Icon name="copy" size={16} />
                <ItemLabel text="Duplicate" tag="span" />
              </Item>
              <Item type="button" role="menuitem" $destructive onClick={choose(onDelete)}>
                <Icon name="trash" size={16} color={STATUS_COLORS.negative} />
                <ItemLabel text="Delete" tag="span" />
              </Item>
            </Panel>,
            document.body,
          )
        : null}
    </>
  );
};

export default StyleCardMenu;
